import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { storage } from '../lib/firebase';
import { db } from '../lib/firebase';
import { Loader2, ChevronLeft, ChevronRight, X } from 'lucide-react';

const Gallery = () => {
  const [images, setImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        
        // Зареждаме само файлове, които са маркирани като галерийни
        const q = query(collection(db, 'media'));
        const querySnapshot = await getDocs(q);
        
        const galleryImages: string[] = [];
        
        querySnapshot.docs.forEach(doc => {
          const data = doc.data();
          const fileName = data.name || '';
          
          // Проверяваме дали файлът е галериен според същата логика от MediaLibrary
          const isGalleryImage = [
            /^molino\d+\.(jpg|jpeg|png|gif)$/i,
            /^logo\.(jpg|jpeg|png|gif)$/i,
            /^hero\.(jpg|jpeg|png|gif)$/i,
            /^melnica\.(jpg|jpeg|png|gif)$/i
          ].some(pattern => pattern.test(fileName));
          
          if (isGalleryImage && data.url) {
            galleryImages.push(data.url);
          }
        });
        
        // Сортираме снимките - първо molino файловете по номер, после останалите
        galleryImages.sort((a, b) => {
          const getFileName = (url: string) => {
            try {
              return decodeURIComponent(url.split('/').pop()?.split('?')[0] || '');
            } catch {
              return url.split('/').pop()?.split('?')[0] || '';
            }
          };
          
          const aName = getFileName(a);
          const bName = getFileName(b);
          
          const aMolino = aName.match(/molino(\d+)/i);
          const bMolino = bName.match(/molino(\d+)/i);
          
          if (aMolino && bMolino) {
            return parseInt(aMolino[1]) - parseInt(bMolino[1]);
          }
          if (aMolino && !bMolino) return -1;
          if (!aMolino && bMolino) return 1;
          
          return aName.localeCompare(bName);
        });

        setImages(galleryImages);
      } catch (error) {
        console.error('Грешка при зареждане на снимките:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  // Auto-slide functionality
  useEffect(() => {
    if (images.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % images.length);
      }, 5000); // Сменя снимката на всеки 5 секунди

      return () => clearInterval(interval);
    }
  }, [images.length]);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (selectedImage === null) return;

    if (e.key === 'Escape') {
      closeLightbox();
    } else if (e.key === 'ArrowLeft') {
      setSelectedImage((prev) => prev === null ? null : (prev - 1 + images.length) % images.length);
    } else if (e.key === 'ArrowRight') {
      setSelectedImage((prev) => prev === null ? null : (prev + 1) % images.length);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, images.length]);

  if (loading) {
    return (
      <section className="py-20 bg-white" id="gallery">
        <div className="container-custom">
          <h2 className="section-title text-center">Галерия</h2>
          <div className="flex items-center justify-center h-96">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </div>
      </section>
    );
  }

  if (images.length === 0) {
    return (
      <section className="py-20 bg-white" id="gallery">
        <div className="container-custom">
          <h2 className="section-title text-center">Галерия</h2>
          <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
            <p className="text-gray-500">Няма налични снимки</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="py-20 bg-white" id="gallery">
        <div className="container-custom">
          <h2 className="section-title text-center">Галерия</h2>
          <p className="text-center text-neutral-600 mb-12 max-w-2xl mx-auto">
            Разгледайте снимки от сградата и околността
          </p>

          {/* Main Slider */}
          <div className="relative mb-8">
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-lg">
              <img
                src={images[currentSlide]}
                alt={`Снимка ${currentSlide + 1}`}
                className="w-full h-full object-cover cursor-pointer transition-opacity duration-500"
                onClick={() => openLightbox(currentSlide)}
              />
              
              {images.length > 1 && (
                <>
                  <button
                    onClick={handlePrevSlide}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  
                  <button
                    onClick={handleNextSlide}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>

                  {/* Slide indicators */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    {images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-3 h-3 rounded-full transition-all ${
                          index === currentSlide
                            ? 'bg-white'
                            : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Thumbnail Grid */}
          {images.length > 1 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {images.map((image, index) => (
                <div
                  key={index}
                  className={`relative aspect-video rounded-lg overflow-hidden cursor-pointer transition-all duration-300 ${
                    index === currentSlide
                      ? 'ring-4 ring-primary shadow-lg'
                      : 'hover:shadow-md hover:scale-105'
                  }`}
                  onClick={() => setCurrentSlide(index)}
                >
                  <img
                    src={image}
                    alt={`Миниатюра ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            onClick={closeLightbox}
          >
            <X className="w-8 h-8" />
          </button>
          
          {images.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage((prev) => prev === null ? null : (prev - 1 + images.length) % images.length);
                }}
              >
                <ChevronLeft className="w-12 h-12" />
              </button>

              <button
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage((prev) => prev === null ? null : (prev + 1) % images.length);
                }}
              >
                <ChevronRight className="w-12 h-12" />
              </button>
            </>
          )}

          <img
            src={images[selectedImage]}
            alt={`Снимка ${selectedImage + 1}`}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white">
            {selectedImage + 1} / {images.length}
          </div>
        </div>
      )}
    </>
  );
};

export default Gallery;